import { RouterSelectors } from '@next/core-lib/router-store';
import { createSelector, createFeatureSelector } from '@ngrx/store';

import {
  AppState,
  TemplateTableItem,
  Template,
  Crop,
  PaginationState,
  SoilType,
} from '../shared/models';
import { selectSoilTypes } from '../soil-type/store/soil-type.selectors';

export const selectApp = createFeatureSelector<AppState>('app');

export const selectTemplates = createSelector(
  selectApp,
  (store: AppState) => store.templates || [],
);

export const selectCrops = createSelector(selectApp, (store: AppState) => store.crops || []);

export const selectSalesOrderPeriods = createSelector(
  selectApp,
  (store: AppState) => store.salesOrderPeriods || [],
);

export const getCropYear = createSelector(selectApp, (store) => store.cropYear);

export const getDefaultCropYear = createSelector(selectApp, (store) => store.defaultCropYear);

export const isDefaultCropYear = createSelector(
  getCropYear,
  getDefaultCropYear,
  (cropYear, defaultCropYear) => cropYear === defaultCropYear,
);

export const selectAvailableCropYears = createSelector(
  selectApp,
  (store: AppState) => store.availableCropYears || [],
);

export const selectTemplateTableItems = createSelector(
  selectTemplates,
  selectCrops,
  selectSoilTypes,
  (templates, crops, soilTypes): TemplateTableItem[] =>
    crops.length > 0 && soilTypes.length > 0
      ? templates.map((template) => ({
          ...template,
          soilTypeName: soilTypes.find((soilType) => soilType.id === template.soilTypeId)?.name,
          cropName: crops.find((crop) => crop.id === template.cropId)?.name,
        }))
      : [],
);

export const selectTemplateById = createSelector(
  selectApp,
  (store: AppState, id: number): Template =>
    store.templates ? store.templates.find((template) => template.id === id) : null,
);

export const selectTemplateTableItem = createSelector(
  selectTemplates,
  selectCrops,
  selectSoilTypes,
  (
    templates: Template[],
    crops: Crop[],
    soilTypes: SoilType[],
    id: number,
  ): TemplateTableItem => {
    if (!templates.length || !crops.length || !soilTypes.length) {
      return;
    }

    const template = templates.find((t) => t.id === id);

    if (!template) {
      return;
    }

    return {
      ...template,
      soilTypeName: soilTypes.find((soilType) => soilType.id === template.soilTypeId)?.name,
      cropName: crops.find((crop) => crop.id === template.cropId)?.name,
    };
  },
);

export const selectEditingTreatments = createSelector(
  selectTemplateTableItem,
  (templateTableItem) => templateTableItem.treatments,
);

export const isTemplatesSet = createSelector(selectApp, (store: AppState) =>
  store.templates ? true : false,
);

export const isTemplateIdValid = createSelector(
  selectTemplates,
  (templates: Template[], id: number) => templates.find((template) => template.id === id) != null,
);

export const getTemplatePagination = createSelector(
  selectApp,
  (state) =>
    state.templatePagination || {
      pageNumber: null,
      pageSize: null,
      totalNumberOfItems: null,
    },
);

export const getTemplateTotalNumberOfItems = createSelector(
  getTemplatePagination,
  (pagination: PaginationState) => pagination.totalNumberOfItems,
);

export const getTemplateFilter = createSelector(selectApp, (state) => state.templateFilter);

export const selectQueryCropYear = RouterSelectors.getQueryParam('cropYear');

export const selectNutrientProperties = createSelector(
  selectApp,
  (store: AppState) => store.nutrientProperties || [],
);

export const selectNutrientSources = createSelector(
  selectApp,
  (store: AppState) => store.nutrientSources || [],
);

export const selectNutrientDesignations = createSelector(
  selectApp,
  (store: AppState) => store.nutrientDesignations || [],
);

export const selectNutrientPropertiesSoilTypes = createSelector(
  selectApp,
  (store: AppState) => store.soilTypes || [],
);

export const selectUnitsOfMeasure = createSelector(
  selectApp,
  (store: AppState) => store.unitsOfMeasure || [],
);

export const selectNutrientSoilTypes = createSelector(
  selectApp,
  (store: AppState) => store.soilTypes || [],
);

export const selectVarietyProperties = createSelector(
  selectApp,
  (store: AppState) => store.varietyProperties || [],
);

export const selectVarietyPropertiesGroupedByEdiCode = createSelector(
  selectVarietyProperties,
  (varietyProperties) => {
    const groups: { ediCode: number; items: typeof varietyProperties }[] = [];
    const map = new Map<number, typeof varietyProperties>();
    for (const vp of (varietyProperties || [])) {
      const key = vp.ediCode;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(vp);
    }
    for (const [ediCode, items] of map.entries()) {
      groups.push({ ediCode, items });
    }
    return groups;
  },
);

export const selectCropVarieties = createSelector(
  selectApp,
  (store: AppState) => store.cropVarieties || [],
);

export const selectProductionPurposeCategories = createSelector(
  selectApp,
  (store: AppState) => store.productionPurposeCategories || [],
);

export const selectProductionPurposeCategoriesDetailed = createSelector(
  selectApp,
  (store: AppState) => store.productionPurposeCategoriesDetailed || [],
);

export const selectNitrogenModelProperties = createSelector(
  selectApp,
  (store: AppState) => store.nitrogenModelProperties || [],
);
